import React, { useEffect, useState } from 'react';
import { f1Markets } from '../odds';
import api from '../api';
import { fmtDateTime } from '../utils/time';
import { demoF1Live } from '../dev/demo';
import EmptyState from './EmptyState';
import { fmtOdds } from '../utils/oddsFmt';

// F1: one card per race (last result + next ones). The race within 7 days of lights out
// opens like a game: winner, top 3 / 6 / 10, head-to-head, pole, fastest lap, winning team.

function countdown(ms, lang) {
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return lang === 'fr' ? `dans ${d} j ${h} h` : `in ${d}d ${h}h`;
  if (h > 0) return lang === 'fr' ? `dans ${h} h ${m} min` : `in ${h}h ${m}m`;
  return lang === 'fr' ? `dans ${m} min` : `in ${m}m`;
}
const when = (iso, lang) => fmtDateTime(iso, lang);   // device time zone + 12 h / 24 h
// F1: drivers by their last name only (Antonelli, Verstappen, Pérez…)
const surname = (name) => { const p = String(name || '').trim().split(/\s+/); return p[p.length - 1] || name; };
// driver photo in a ring of his team colour (initials when there's no photo)
const Face = ({ d, size = 28 }) => (
  <span className="f1-face" style={{ '--sz': `${size}px`, '--tc': d?.color || 'var(--fl-line)' }}>
    {d?.photo ? <img alt="" src={d.photo} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <b>{(d?.code || '').slice(0, 3)}</b>}
  </span>
);
// the feeds name teams a bit differently ("Red Bull" / "Red Bull Racing", "Haas" / "Haas F1 Team", "RB F1 Team" / "Racing Bulls")
const F1_LOGO_SLUGS = [[/racing bulls|^rb |alphatauri/i, 'racingbulls'], [/red bull/i, 'redbullracing'], [/alpine/i, 'alpine'], [/aston/i, 'astonmartin'], [/audi|sauber/i, 'audi'], [/cadillac/i, 'cadillac'], [/ferrari/i, 'ferrari'], [/haas/i, 'haasf1team'], [/mclaren/i, 'mclaren'], [/mercedes/i, 'mercedes'], [/williams/i, 'williams']];
export const teamLogo = (team) => {
  const slug = F1_LOGO_SLUGS.find(([re]) => re.test(team || ''))?.[1];
  if (slug === 'mercedes') return '/brand/f1-mercedes.svg';   // the F1 site's one is too thin at this size
  return slug ? `https://media.formula1.com/image/upload/v1740000001/common/f1/2026/${slug}/2026${slug}logo.svg` : null;
};
// the team's logo, like the logos beside players in the other sports (its colour if the logo doesn't load)
function TeamLogo({ team, color, size = 22 }) {
  const [broken, setBroken] = useState(false);
  const src = teamLogo(team);
  if (!src || broken) return <i className="f1-teamdot" style={{ background: color || 'var(--fl-line)' }} />;
  // these logos have a lot of empty room around them: drawn bigger, taking up the same space
  return <img className="f1-teamlogo" alt="" src={src} style={{ width: size * 1.5, height: size * 1.5, margin: `${size * -0.25}px 0` }} onError={() => setBroken(true)} />;
}
// the phone has no room for two full names side by side: there, last name only (and no "F1 Team")
const DriverName = ({ name }) => <span className="pg-name"><span className="f1-first">{String(name).slice(0, -surname(name).length)}</span>{surname(name)}</span>;
// days : hours : minutes : seconds, ticking every second
function Countdown({ to, t }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const ms = Math.max(0, new Date(to).getTime() - now);
  const parts = [[Math.floor(ms / 86400000), t.cdDays], [Math.floor((ms % 86400000) / 3600000), t.cdHours], [Math.floor((ms % 3600000) / 60000), t.cdMin], [Math.floor((ms % 60000) / 1000), t.cdSec]];
  return (
    <div className="f1-cd">
      {parts.map(([v, label]) => <span key={label}><b>{String(v).padStart(2, '0')}</b><small>{label}</small></span>)}
    </div>
  );
}
// the podium stand: 2 · 1 · 3
function PodiumStand({ podium, t }) {
  const at = (n) => podium.find((p) => p.position === n);
  return (
    <div className="f1-stand">
      {[2, 1, 3].map((n) => {
        const p = at(n);
        if (!p) return <div key={n} />;
        return (
          <div key={n} className={`f1-step p${n}`}>
            <Face d={p} size={n === 1 ? 76 : 60} />
            <b className="f1-step-name">{surname(p.name)}</b>
            <small className="f1-step-team">{p.team}</small>
            <div className="f1-step-block" style={{ '--tc': p.color || 'var(--accent)' }}>
              <span className="f1-step-pos">{n}</span>
              <small>{n === 1 ? (p.gap || t.winnerShort) : p.gap}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// tyre chip: S / M / H / I / W in the Pirelli colours
const TYRES = { SOFT: 'S', MEDIUM: 'M', HARD: 'H', INTERMEDIATE: 'I', WET: 'W' };
const Tyre = ({ c }) => <i className={`f1-tyre ${String(c || '').toLowerCase()}`} title={c}>{TYRES[c] || '?'}</i>;

// championship standings, under the races
function Standings({ lang, t, favTeam }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('drivers');
  useEffect(() => { api.fetchF1Standings().then((d) => d && setData(d)).catch(() => {}); }, []);
  if (!data) return null;
  const rows = tab === 'drivers' ? data.drivers : data.constructors;
  const leader = rows[0]?.points || 1;
  return (
    <div className="game-card f1-card f1-standings">
      <div className="f1-standings-head">
        <b>{t.championship} {data.season}</b>
        <span className="f1-standings-sub">{t.afterRound} {data.round}</span>
        <div className="f1-standings-tabs">
          <button type="button" className={tab === 'drivers' ? 'active' : ''} onClick={() => setTab('drivers')}>{t.drivers}</button>
          <button type="button" className={tab === 'constructors' ? 'active' : ''} onClick={() => setTab('constructors')}>{t.constructors}</button>
        </div>
      </div>
      <div className="f1-order">
        {rows.map((r) => (
          <div key={r.code || r.team} className={`f1-row f1-standing${r.pos <= 3 ? ' podium' : ''}${sameTeam(r.team, favTeam) ? ' fav' : ''}`}>
            <span className="f1-pos">{r.pos}</span>
            <span className="f1-driver f1-driver-face">{r.name ? <Face d={r} size={30} /> : <TeamLogo team={r.team} color={r.color} size={26} />}<span><b>{r.name ? surname(r.name) : r.team}</b>{r.name && <small>{r.team}</small>}</span></span>
            <span className="f1-points-bar"><i style={{ width: `${(r.points / leader) * 100}%`, background: r.color || undefined }} /></span>
            <span className="f1-wins">{r.wins ? `${r.wins} ${r.wins === 1 ? t.win : t.wins}` : ''}</span>
            <span className="f1-gap"><b>{r.points}</b> {t.pts}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// your favorite F1 team (My Teams), matched loosely: the feeds name teams a bit differently ("Red Bull" / "Red Bull Racing", "Haas" / "Haas F1 Team")
const sameTeam = (a, b) => !!a && !!b && (a.toLowerCase().includes(b.toLowerCase().split(' ')[0]) || b.toLowerCase().includes(a.toLowerCase().split(' ')[0]));

export default function F1Races({ races, lang = 'en', expandedId, onToggle, onBet, empty, favTeam = '' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((x) => x + 1), 30 * 1000); return () => clearInterval(id); }, []);
  // the opened race's running order: every 15 s while live, once when final
  const [timing, setTiming] = useState({});
  const [view, setView] = useState('races');
  const openRace = (races || []).find((r) => r.id === expandedId);
  const openState = openRace?.status;
  useEffect(() => {
    if (!openRace || !['LIVE', 'OFF'].includes(openState)) return undefined;
    const load = () => {
      const demo = demoF1Live(openRace);
      if (demo) { setTiming((x) => ({ ...x, [openRace.id]: demo })); return; }
      api.fetchF1Live(openRace.id).then((d) => d && setTiming((x) => ({ ...x, [openRace.id]: d }))).catch(() => {});
    };
    load();
    if (openState !== 'LIVE') return undefined;
    const id = setInterval(load, 15 * 1000);
    return () => clearInterval(id);
  }, [expandedId, openState]);   // eslint-disable-line

  // Races | Standings, at the top of the F1 page
  const tabs = (
    <div className="f1-page-tabs">
      <button type="button" className={view === 'races' ? 'active' : ''} onClick={() => setView('races')}>{t.races}</button>
      <button type="button" className={view === 'standings' ? 'active' : ''} onClick={() => setView('standings')}>{t.standings}</button>
    </div>
  );
  if (view === 'standings') return <>{tabs}<div className="games-grid f1-grid"><Standings lang={lang} t={t} favTeam={favTeam} /></div></>;
  if (!races) return <>{tabs}<EmptyState icon="loading" title={t.loading} /></>;
  if (!races.length) return <>{tabs}{empty || <EmptyState icon="flag" title={t.none} />}</>;

  const nextRace = races.find((r) => r.status === 'FUT');
  const ordered = nextRace ? [nextRace, ...races.filter((r) => r !== nextRace)] : races;
  return (<>
    {tabs}
    <div className="games-grid f1-grid">
      {ordered.map((race) => {
        const hero = race === nextRace;
        const start = new Date(race.race_time).getTime();
        const open = race.bettable && race.status === 'FUT';
        const canOpen = open || race.status === 'LIVE' || race.status === 'OFF';
        const expanded = canOpen && expandedId === race.id;
        const mk = expanded && open ? f1Markets(race) : null;
        const tm = timing[race.id];
        const cd = countdown(start - Date.now(), lang);
        return (
          <div key={race.id} className={`game-card f1-card${hero ? ' f1-hero' : ''}${expanded ? ' expanded' : ''}${race.status === 'OFF' ? ' finished-game' : ''}${race.status === 'LIVE' ? ' live-game' : ''}`}>
            <div className={`f1-head${canOpen ? ' clickable' : ''}`} onClick={() => canOpen && onToggle(race.id)}>
              {race.flag ? <img className="f1-flag" alt="" src={`https://flagcdn.com/w80/${race.flag}.png`} /> : <span className="f1-flag" />}
              <div className="f1-title">
                <span className="f1-round">{t.round} {race.round}{race.sprint ? ` · ${t.sprint}` : ''}</span>
                <b className="f1-name">{race.name}</b>
                <span className="f1-circuit">{race.circuit}{race.locality ? ` · ${race.locality}` : ''}</span>
              </div>
              <div className="f1-when">
                {hero && <span className="f1-next">{t.nextRace}</span>}
                {race.status === 'OFF' ? <span className="f1-badge final">{t.final}</span>
                  : race.status === 'LIVE' ? <span className="f1-badge live">{t.live}</span>
                  : <span className="f1-badge">{cd || t.soon}</span>}
                <span className="f1-date">{when(race.race_time, lang)}</span>
              </div>
            </div>

            {hero && (
              <div className="f1-hero-body">
                <Countdown to={race.race_time} t={t} />
                <div className="f1-sessions">
                  {(race.sessions || []).map((s) => {
                    const past = new Date(s.time).getTime() < Date.now();
                    return (
                      <span key={s.key} className={`f1-session${past ? ' past' : ''}${s.key === 'race' ? ' race' : ''}`}>
                        <b>{t.sess[s.key] || s.key}</b>
                        <small>{when(s.time, lang)}</small>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {expanded && race.status !== 'FUT' && (
              <div className="f1-timing">
                <div className="f1-timing-head">
                  <b>{race.status === 'LIVE' ? t.runningOrder : t.classification}</b>
                  {tm?.lap ? <span className="f1-lap">{t.lap} {tm.lap}{tm.totalLaps ? ` / ${tm.totalLaps}` : ''}</span>
                    : tm?.totalLaps && race.status === 'OFF' ? <span className="f1-lap">{tm.totalLaps} {t.laps}</span> : null}
                </div>
                {tm?.weather && (
                  <div className="f1-weather">
                    <span>{t.air} <b>{Math.round(tm.weather.air)}°</b></span>
                    <span>{t.track} <b>{Math.round(tm.weather.track)}°</b></span>
                    <span>{t.humidity} <b>{Math.round(tm.weather.humidity)}%</b></span>
                    <span className={tm.weather.rain ? 'rain' : ''}><b>{tm.weather.rain ? t.rain : t.dry}</b></span>
                  </div>
                )}
                {!tm ? <p className="f1-note">{t.loadingTiming}</p>
                  : tm.available === false ? <p className="f1-note">{t.noTiming}</p>
                  : (() => {
                    const live = race.status === 'LIVE';
                    return (
                      <div className={`f1-order ${live ? 'is-live' : 'is-final'}`}>
                        <div className="f1-row f1-row-head">
                          <span className="f1-pos">P</span><span />
                          <span className="f1-driver">{t.driver}</span>
                          <span className="f1-tyre-col">{live ? t.tyre : t.strategy}</span>
                          <span className="f1-lapcol">{live ? t.lastLap : t.gridCol}</span>
                          <span className="f1-lapcol">{t.bestLap}</span>
                          <span className="f1-gap">{live ? t.gapCol : t.timeCol}</span>
                        </div>
                        {tm.order.map((d) => (
                          <div key={d.code} className={`f1-row${d.pos <= 3 ? ' podium' : ''}${sameTeam(d.team, favTeam) ? ' fav' : ''}`}>
                            <span className="f1-pos">{d.pos}</span>
                            <Face d={d} size={28} />
                            <span className="f1-driver"><b>{surname(d.name)}</b><small>{d.team}{d.pits ? ` · ${d.pits} ${t.pit}` : ''}</small></span>
                            <span className="f1-tyre-col">
                              {live
                                ? (d.tyre ? <><Tyre c={d.tyre.compound} /><small>{d.tyre.age} {t.lapsShort}</small></> : null)
                                : (d.strategy || []).map((c, i) => <Tyre key={i} c={c} />)}
                            </span>
                            <span className="f1-lapcol">
                              {live ? d.last
                                : d.grid ? <>{d.grid}{d.gained ? <em className={d.gained > 0 ? 'up' : 'down'}>{d.gained > 0 ? `+${d.gained}` : d.gained}</em> : null}</> : '—'}
                            </span>
                            <span className={`f1-lapcol${d.fastest ? ' fastest' : ''}`}>{d.best}</span>
                            <span className="f1-gap">{d.gap === 'LEADER' ? t.leader : d.gap}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                {tm?.control?.length > 0 && (
                  <div className="pbp-feed f1-control">
                    <h5>{t.raceControl}</h5>
                    <div className="pbp-list">
                      {tm.control.map((m, i) => (
                        <div key={i} className={`pbp-play ${m.category === 'SafetyCar' || m.flag === 'RED' ? 'goal' : ''}`}>
                          <span className="pbp-time">{m.lap ? `${t.lap.toUpperCase()} ${m.lap}` : ''}</span>
                          <span className={`pbp-team f1-flagchip ${String(m.flag || m.category).toLowerCase().replace(/\s+/g, '-')}`}>{m.category === 'SafetyCar' ? 'SC' : m.flag || (/PENALTY/i.test(m.message) ? t.penalty : 'FIA')}</span>
                          <span className="pbp-desc">{m.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {race.status === 'OFF' && race.podium?.length > 0 && !expanded && (
              <PodiumStand podium={race.podium} t={t} />
            )}
            {race.status === 'FUT' && !race.bettable && (
              <p className="f1-note">{Date.now() < new Date(race.opens_at).getTime() ? `${t.opens} ${when(race.opens_at, lang)}` : t.preparing}</p>
            )}
            {open && !expanded && <p className="f1-note">{t.tap}</p>}
            {!open && canOpen && !expanded && <p className="f1-note">{race.status === 'LIVE' ? t.tapLive : t.tapFinal}</p>}

            {expanded && open && race.grid?.length > 0 && (
              <div className="f1-timing">
                <div className="f1-timing-head"><b>{t.startingGrid}</b><span className="f1-lap">{t.qualifying}</span></div>
                <div className="f1-order is-grid">
                  <div className="f1-row f1-row-head">
                    <span className="f1-pos">P</span><span className="f1-driver">{t.driver}</span>
                    <span className="f1-lapcol">Q1</span><span className="f1-lapcol">Q2</span><span className="f1-lapcol">Q3</span>
                  </div>
                  {race.grid.map((g) => (
                    <div key={g.code} className={`f1-row${g.pos === 1 ? ' podium' : ''}${sameTeam(g.team, favTeam) ? ' fav' : ''}`}>
                      <span className="f1-pos">{g.pos}</span>
                      <span className="f1-driver f1-driver-face"><Face d={g} size={26} /><span><b>{surname(g.name)}</b><small>{g.team}</small></span></span>
                      <span className="f1-lapcol">{g.q1}</span><span className="f1-lapcol">{g.q2}</span><span className="f1-lapcol">{g.q3}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expanded && mk && (
              <div className="markets">
                <div className="market">
                  <h5>{t.winner}</h5>
                  <p className="market-desc">{t.winnerDesc}</p>
                  <div className="player-grid">
                    {mk.drivers.map((d) => (
                      <button key={d.code} disabled={!d.odds.f1_winner} onClick={() => onBet(race, 'f1_winner', d.code, { driver: surname(d.name) })}>
                        <span className="pg-player"><Face d={d} size={22} /><DriverName name={d.name} /></span> <span>{fmtOdds(d.odds.f1_winner)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="market">
                  <h5>{t.finish}</h5>
                  <p className="market-desc">{t.finishDesc}</p>
                  <div className="f1-fin">
                    <div className="f1-fin-row f1-fin-head">
                      <span>{t.driver}</span><span>{t.top3}</span><span>{t.top6}</span><span>{t.top10}</span>
                    </div>
                    {mk.drivers.map((d) => (
                      <div key={d.code} className="f1-fin-row">
                        <span className="f1-fin-driver"><Face d={d} size={22} /><span><b>{d.name}</b></span></span>
                        {['f1_top3', 'f1_top6', 'f1_top10'].map((bt) => (
                          <button key={bt} type="button" className="f1-fin-odd" disabled={!d.odds[bt]} onClick={() => onBet(race, bt, d.code, { driver: surname(d.name) })}>
                            {fmtOdds(d.odds[bt])}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="market">
                  <h5>{t.h2h}</h5>
                  <p className="market-desc">{t.h2hDesc}</p>
                  <div className="f1-h2h">
                    {mk.h2h.map((x) => {
                      const A = mk.drivers.find((d) => d.code === x.a), B = mk.drivers.find((d) => d.code === x.b);
                      return (
                        <div key={x.team} className="f1-h2h-row">
                          <span className="f1-h2h-team"><TeamLogo team={x.team} color={A?.color} /><span>{x.team}</span></span>
                          <div className="player-grid f1-h2h-pair">
                            <button onClick={() => onBet(race, 'f1_h2h', x.a, { driver: surname(A?.name || x.a), vs: x.b, vsName: surname(B?.name || x.b) })}><span className="pg-player"><Face d={A} size={22} /><DriverName name={A?.name || x.a} /></span> <span>{fmtOdds(x.oddsA)}</span></button>
                            <button onClick={() => onBet(race, 'f1_h2h', x.b, { driver: surname(B?.name || x.b), vs: x.a, vsName: surname(A?.name || x.a) })}><span className="pg-player"><Face d={B} size={22} /><DriverName name={B?.name || x.b} /></span> <span>{fmtOdds(x.oddsB)}</span></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="market">
                  <h5>{t.pole}</h5>
                  <p className="market-desc">{mk.poleOpen ? `${t.poleDesc} ${race.quali_time ? when(race.quali_time, lang) : ''}` : t.poleClosed}</p>
                  {mk.poleOpen && (
                    <div className="player-grid">
                      {mk.drivers.map((d) => (
                        <button key={d.code} disabled={!d.odds.f1_pole} onClick={() => onBet(race, 'f1_pole', d.code, { driver: surname(d.name) })}>
                          <span className="pg-player"><Face d={d} size={22} /><DriverName name={d.name} /></span> <span>{fmtOdds(d.odds.f1_pole)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="market">
                  <h5>{t.fl}</h5>
                  <p className="market-desc">{t.flDesc}</p>
                  <div className="player-grid">
                    {mk.drivers.map((d) => (
                      <button key={d.code} disabled={!d.odds.f1_fastest_lap} onClick={() => onBet(race, 'f1_fastest_lap', d.code, { driver: surname(d.name) })}>
                        <span className="pg-player"><Face d={d} size={22} /><DriverName name={d.name} /></span> <span>{fmtOdds(d.odds.f1_fastest_lap)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="market">
                  <h5>{t.team}</h5>
                  <p className="market-desc">{t.teamDesc}</p>
                  <div className="player-grid">
                    {mk.constructors.map((c) => (
                      <button key={c.team} disabled={!c.odds} onClick={() => onBet(race, 'f1_constructor', c.team, { team: c.team })}>
                        <span className="pg-player"><TeamLogo team={c.team} color={c.color} /><span className="pg-name">{c.team.replace(/ F1 Team$/, '')}<span className="f1-first">{/ F1 Team$/.test(c.team) ? ' F1 Team' : ''}</span></span></span> <span>{fmtOdds(c.odds)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </>);
}

const TR = {
  en: {
    loading: 'Loading races…', none: 'No races right now', round: 'Round', sprint: 'Sprint weekend', final: 'Final', live: 'Live', soon: 'Soon',
    opens: 'Bets open', preparing: 'Odds coming soon', tap: 'Open the race to bet', tapLive: 'Open the race for the running order', tapFinal: 'Open the race for the full classification',
    winner: 'Race winner', winnerDesc: 'Who takes the checkered flag first',
    finish: 'Finishing position', finishDesc: 'Finish in the top 3 (podium), top 6 or top 10 (points)',
    top3: 'Top 3', top6: 'Top 6', top10: 'Top 10',
    h2h: 'Teammate head-to-head', h2hDesc: 'Who finishes ahead of his teammate',
    pole: 'Pole position', poleDesc: 'Fastest in qualifying. Closes when qualifying starts:', poleClosed: 'Closed: qualifying has started.',
    fl: 'Fastest lap', flDesc: 'Sets the fastest lap of the race',
    team: 'Winning team', teamDesc: 'The team of the race winner',
    runningOrder: 'Running order', classification: 'Final classification', lap: 'Lap', laps: 'laps', leader: 'Leader', pit: 'stop(s)', fastest: 'Fastest lap',
    driver: 'Driver', tyre: 'Tyre', strategy: 'Tyres', lastLap: 'Last lap', bestLap: 'Best lap', gapCol: 'Gap', timeCol: 'Time / gap', gridCol: 'Grid', lapsShort: 'laps',
    air: 'Air', track: 'Track', humidity: 'Humidity', rain: 'Rain', dry: 'Dry', raceControl: 'Race control', penalty: 'PEN',
    startingGrid: 'Starting grid', qualifying: 'Qualifying',
    races: 'Races', standings: 'Standings', nextRace: 'Next race', winnerShort: 'Winner',
    cdDays: 'days', cdHours: 'hrs', cdMin: 'min', cdSec: 'sec',
    sess: { fp1: 'Practice 1', fp2: 'Practice 2', fp3: 'Practice 3', sprintQuali: 'Sprint quali', sprint: 'Sprint', quali: 'Qualifying', race: 'Race' },
    championship: 'Championship', afterRound: 'after round', drivers: 'Drivers', constructors: 'Teams', win: 'win', wins: 'wins', pts: 'pts',
    loadingTiming: 'Loading timing…', noTiming: 'Live timing isn’t available right now. The full classification shows here after the race.',
  },
  fr: {
    loading: 'Chargement des courses…', none: 'Aucune course pour le moment', round: 'Manche', sprint: 'Week-end sprint', final: 'Terminé', live: 'En direct', soon: 'Bientôt',
    opens: 'Paris ouverts', preparing: 'Cotes bientôt disponibles', tap: 'Ouvre la course pour parier', tapLive: 'Ouvre la course pour l’ordre en direct', tapFinal: 'Ouvre la course pour le classement complet',
    winner: 'Vainqueur de la course', winnerDesc: 'Qui voit le drapeau à damier en premier',
    finish: 'Position à l’arrivée', finishDesc: 'Finir dans le top 3 (podium), le top 6 ou le top 10 (points)',
    top3: 'Top 3', top6: 'Top 6', top10: 'Top 10',
    h2h: 'Duel de coéquipiers', h2hDesc: 'Qui finit devant son coéquipier',
    pole: 'Pole position', poleDesc: 'Le plus rapide en qualifications. Ferme au début des qualifs :', poleClosed: 'Fermé : les qualifications ont commencé.',
    fl: 'Tour le plus rapide', flDesc: 'Signe le meilleur tour en course',
    team: 'Équipe gagnante', teamDesc: 'L’équipe du vainqueur',
    runningOrder: 'Ordre de course', classification: 'Classement final', lap: 'Tour', laps: 'tours', leader: 'En tête', pit: 'arrêt(s)', fastest: 'Meilleur tour',
    driver: 'Pilote', tyre: 'Pneu', strategy: 'Pneus', lastLap: 'Dernier tour', bestLap: 'Meilleur tour', gapCol: 'Écart', timeCol: 'Temps / écart', gridCol: 'Grille', lapsShort: 'tours',
    air: 'Air', track: 'Piste', humidity: 'Humidité', rain: 'Pluie', dry: 'Sec', raceControl: 'Direction de course', penalty: 'PÉN',
    startingGrid: 'Grille de départ', qualifying: 'Qualifications',
    races: 'Courses', standings: 'Classement', nextRace: 'Prochaine course', winnerShort: 'Vainqueur',
    cdDays: 'jours', cdHours: 'h', cdMin: 'min', cdSec: 's',
    sess: { fp1: 'Essais 1', fp2: 'Essais 2', fp3: 'Essais 3', sprintQuali: 'Qualifs sprint', sprint: 'Sprint', quali: 'Qualifications', race: 'Course' },
    championship: 'Championnat', afterRound: 'après la manche', drivers: 'Pilotes', constructors: 'Équipes', win: 'victoire', wins: 'victoires', pts: 'pts',
    loadingTiming: 'Chargement du chrono…', noTiming: 'Le chrono en direct n’est pas disponible pour le moment. Le classement complet s’affiche ici après la course.',
  },
};
